<script setup lang="ts">
type Props = {
  showDynamicNumbers?: boolean
  polygon?: string
  theme?: string
  media: object
  title: string
  subtitle: string
  svgIcon: string
  geopoint?: {
    lat: number
    lng: number
  }
  items?: {
    _key: string
    title: string
    info: string
  }[]
}

const _props = defineProps<{ data: Props }>()
const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

const { data: speciesData } = await useSpeciesData(_props.data?.polygon)
const { t } = useI18n()

// If geopoint is provided, use it to get weather data from /api/weather with the props lat and lon
const { data: weatherData } = await useFetch(
  `/api/weather?lat=${_props.data?.geopoint?.lat}&lon=${_props.data?.geopoint?.lng}`,
)

// If weather format the date in wather celsium and sunrise and sunset
const temperature = computed(() => {
  return weatherData.value?.minTemp && weatherData.value?.maxTemp
    ? `${weatherData.value.minTemp} - ${weatherData.value.maxTemp}°C`
    : ''
})

const sunriseSunset = computed(() => {
  return weatherData.value?.sunrise && weatherData.value?.sunset
    ? `${weatherData.value.sunrise.split('T')[1]} - ${weatherData.value.sunset.split('T')[1]}`
    : ''
})

const infoItems = computed(() => {
  if (_props.data.showDynamicNumbers) {
    // Only show dynamic items if showDynamicNumbers is true
    return [
      {
        info: speciesData.value.allSpecies.total.toLocaleString('da-DK'),
        suffix: '',
        title: t('label.area.discoveredSpecies'),
        _key: 1,
      },
      // {
      //   info: speciesData.value.redlistedSpecies.total,
      //   suffix: '',
      //   title: t('label.area.redlistedSpecies'),
      //   _key: 2,
      // },
      {
        info: temperature.value,
        suffix: '',
        title: t('label.area.weather'),
        _key: 3,
      },
      {
        info: sunriseSunset.value,
        suffix: '',
        title: t('label.area.sunriseSunset'),
        _key: 4,
      },
    ]
  } else {
    // Only show original items if showDynamicNumbers is false
    return _props.data.items
  }
})
</script>

<template>
  <div
    class="hero relative flex h-[100svh] items-end justify-center overflow-clip text-(--theme-fg)"
    :class="`theme-${data?.theme ? data?.theme : 'green-light-green'}`"
  >
    <ElementsThemeSetter :theme="data?.theme" :is-hero="true" />
    <div class="background absolute top-0 left-0 z-1 block h-full w-full">
      <div class="overlay absolute top-0 left-0 z-[1] h-full w-full bg-black opacity-20" />
      <CommonMedia v-if="data?.media" :media="data?.media" class="h-full w-full object-contain" />
    </div>
    <div class="px-bleed relative z-10 flex h-full w-full flex-col pt-16 pb-10 text-(--theme-fg)">
      <div class="py-auto flex h-full flex-col items-center justify-center">
        <h3 class="font-display mb-8 text-lg">{{ data?.subtitle }}</h3>

        <!-- eslint-disable vue/no-v-html -->
        <span
          v-if="data?.svgIcon"
          v-dompurify-html="data?.svgIcon"
          class="svg-wrapper relative max-w-2/3"
        />
        <!-- eslint-enable vue/no-v-html -->

        <span
          v-else
          class="font-display title relative z-2 text-center text-2xl leading-[0.65] text-(--green-4) lg:text-5xl"
        >
          <CommonExposedText
            v-if="isMobile"
            :text="data?.title"
            :exposure="[-40, -80]"
            :animated="true"
          />
          <CommonProximityText
            v-else
            :text="data?.title"
            :radius="700"
            falloff="gaussian"
            :exposure="{ from: -20, to: -80 }"
          />
        </span>
      </div>
      <h1 class="hidden">{{ data?.title }}</h1>
      <ElementsInformationItems :items="infoItems" />
    </div>
  </div>
</template>

<style>
.svg-wrapper svg {
  width: 100%;
  height: auto;
}
</style>
